import { LeftOutlined } from '@ant-design/icons'
import {
  Button,
  Card,
  Col,
  DatePicker,
  Flex,
  Form,
  FormInstance,
  Input,
  Row
} from 'antd'
import dayjs from 'dayjs'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  useCounterpartServiceGetInternalInstitutionEndpoint,
  useStocksServiceCreateIssuanceEndpoint
} from 'src/api/queries'
import { ApiError, CreateInventoryIssuanceItemDto } from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import {
  CustomItemRender,
  FormFooter,
  InternalInstitutionsSelect,
  message,
  Upload
} from 'src/components'
import {
  FileDocument,
  useFileHandler,
  useScrollToFirstError,
  useUser,
  ValidationError
} from 'src/hooks'
import { dayMonthYearBackSlash, yearMonthDaySlash } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { formErrorHandler } from 'src/utils'

import { AdminConsumableCreateList } from './AdminConsumableCreateList'

type Props = {
  form: FormInstance
}

export const StockAdminIssuanceCreate = ({ form }: Props) => {
  const { onScrollToError } = useScrollToFirstError(form)

  const { t } = useLocales()
  const navigate = useNavigate()

  const { userData } = useUser()

  const { onFileChange, saveFiles, isUploading } = useFileHandler({
    form,
    folder: 'issuance'
  })

  const onBackClick = () => {
    navigate(paths.issuance.root)
  }

  const { data } = useCounterpartServiceGetInternalInstitutionEndpoint({
    id: String(userData?.currentLegalEntityId)
  })

  const { mutate, isPending } = useStocksServiceCreateIssuanceEndpoint({
    onSuccess: () => {
      message.success(t('stock_applications.successfully_issued'))
      navigate(paths.stock.applications)
    },
    onError: (e: ApiError) => formErrorHandler(e.body.errors)
  })

  const onSubmit = async () => {
    try {
      const values = await form.validateFields()

      const updatedFiles = await saveFiles()

      mutate({
        requestBody: {
          documentDate: dayjs(values?.documentDate).format(yearMonthDaySlash),
          documentNumber: values.documentNumber,
          contractId: values?.contractId,
          transporterId: values?.transporterId,
          takeoverDate: dayjs(values?.takeoverDate).format(yearMonthDaySlash),
          accompanyingNotice: values?.accompanyingNotice,
          comment: values?.comment,
          mediaItems: updatedFiles,
          items: values?.inquiryItems?.map(
            (item: CreateInventoryIssuanceItemDto) => ({
              inventoryItemId: item?.inventoryItemId,
              quantity: item?.quantity
            })
          )
        }
      })
    } catch (error) {
      onScrollToError(error as ValidationError)
    }
  }

  useEffect(() => {
    if (data) {
      form.setFieldValue('provider', data.name)
    }
  }, [data])

  return (
    <Flex vertical gap={24}>
      <Flex justify="space-between">
        <Button
          icon={<LeftOutlined />}
          className="fit-content"
          onClick={onBackClick}
        >
          {t('common.back')}
        </Button>
      </Flex>

      <Flex vertical gap={16}>
        <Card>
          <Flex vertical gap={16}>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Form.Item
                  name="documentDate"
                  label={t(`stock.invoice_date`)}
                  rules={requiredRules}
                >
                  <DatePicker
                    className="full-width"
                    format={dayMonthYearBackSlash}
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item name="provider" label={t(`counterparts.provider`)}>
                  <Input disabled />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  name="contractId"
                  label={t(`counterparts.purchase_center`)}
                  rules={requiredRules}
                >
                  <InternalInstitutionsSelect />
                </Form.Item>
              </Col>
            </Row>

            <Flex vertical gap={16}>
              <AdminConsumableCreateList form={form} />
            </Flex>
          </Flex>
        </Card>

        <Card>
          <Flex vertical gap={16}>
            <Card title={t('stock_applications.uploaded_documents')}>
              <Form.Item name="docs" className="full-width">
                <Upload
                  onChange={onFileChange}
                  customRequest={() => null}
                  itemRender={(_, file: FileDocument, __, { remove }) => (
                    <CustomItemRender file={file} onRemove={remove} />
                  )}
                />
              </Form.Item>
            </Card>

            <Card title={t('common.comment')}>
              <Flex vertical gap={16}>
                <Flex vertical gap={8}>
                  {t('counterparts.provider')}
                  <Form.Item name="comment">
                    <Input.TextArea
                      rows={5}
                      placeholder={t('common.leave_comment')}
                    />
                  </Form.Item>
                </Flex>
              </Flex>
            </Card>
          </Flex>
        </Card>
      </Flex>

      <FormFooter
        onCancel={onBackClick}
        onSave={() => onSubmit()}
        saveLabel={t('stock_applications.confirm')}
        saveConfirmTitle={`${t('stock_applications.confirm')}?`}
        loading={isUploading || isPending}
      />
    </Flex>
  )
}
